import type { ComponentChildren } from 'preact';
import { useEffect } from 'preact/hooks';

/**
 * 058: a bottom sheet — the app's first modal primitive.
 *
 * Deliberately generic: it knows about being open, being dismissed, and its children.
 * Nothing about filters, so the next screen that needs a panel can reuse it.
 *
 * Uses a native `<dialog>` rather than a `div role="dialog"`. Both are accessible, but the
 * native element is what biome's `useSemanticElements` asks for, and `ConfirmDialog`
 * already carries that warning — one is a known debt, two would be a habit. Rendered with
 * the `open` attribute rather than `showModal()`, because this component draws its own
 * backdrop and must not fight the browser's top-layer behaviour.
 */

interface SheetProps {
  open: boolean;
  /** Names the sheet for assistive technology, and heads it visually. */
  title: string;
  onDismiss: () => void;
  children: ComponentChildren;
  /** Pinned to the bottom of the sheet, outside the scrolling body — Apply / Clear all. */
  footer?: ComponentChildren;
}

export function Sheet({ open, title, onDismiss, children, footer }: SheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onDismiss]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center">
      {/* A button, not a div with onClick: it is reachable by keyboard and needs no
          separate key handler, which is how ConfirmDialog avoids the same lint rule. */}
      <button
        type="button"
        aria-label={`Dismiss ${title}`}
        className="absolute inset-0 bg-black/60"
        onClick={onDismiss}
      />
      <dialog
        open
        aria-label={title}
        className="relative w-full max-w-lg max-h-[85vh] m-0 flex flex-col bg-surface-card text-text rounded-t-2xl p-0 border-0"
      >
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h2 className="text-base font-bold text-text">{title}</h2>
          <button
            type="button"
            aria-label="Close"
            className="text-gray-400 text-xl leading-none px-2"
            onClick={onDismiss}
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-4">{children}</div>
        {footer && <div className="border-t border-gray-800 px-4 py-3 flex gap-2">{footer}</div>}
      </dialog>
    </div>
  );
}
